<template>
  <div class="report-data-source-details grid m-0">
    
<div class="col-12">
    <p v-if="!loading && firstLoadDone" class="report-title">
      {{ report.titulo }}
    </p>
  </div>
    <div v-if="filteredTopSectionFilters.length" class="col-12 pb-0">
      <TopSection
        ref="topSectionRef"
        :filtros="filteredTopSectionFilters"
        :leftSectionFiltersLength="filteredLeftSectionFilters.length"
        @apply-filters="handlerApplyFilters()"
      />
    </div>

    <div
      v-if="filteredLeftSectionFilters.length"
      class="col-12 sm:col-4 md:col-3"
      :class="{ 'left-section-container-mobile': true }"
    >
      <LeftSection
        ref="leftSectionRef"
        :filtros="filteredLeftSectionFilters"
        :topSectionFiltersLength="filteredTopSectionFilters.length"
        @apply-filters="handlerApplyFilters()"
      />
    </div>

    <div v-if="loading" class="flex justify-content-center" :class="tableWidth">
      <ProgressSpinner class="progress-spinner" strokeWidth="3" />
    </div>

    <div v-if="tableColumns.length && !loading" :class="tableWidth">
      <TableSection
        ref="tableSectionRef"
        :title="report.titulo"
        :columns="tableColumns"
        :rows="defineTableRows"
        @downloadRegistersEvent="handleDownloadRegisters"
        @pageChange="handlePageChange"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, computed } from "vue";
import { useToast } from "primevue/usetoast";
import ProgressSpinner from "primevue/progressspinner";
import { isMobile } from "@/composables/_commons/helper.js";

import { downloadExecuteReport } from "@/api/reports/reports.api.js";
import { downloadFile } from "@/composables/_commons/helper.js";

import { getReportTableData } from "@/api/reports/reports.api.js";
import TopSection from "@/pages/_root/views/reports-details/sections/TopSection.vue"; // eslint-disable-line
import LeftSection from "@/pages/_root/views/reports-details/sections/LeftSection.vue"; // eslint-disable-line
import TableSection from "@/pages/_root/views/reports-details/data-source-type/TableSection.vue"; // eslint-disable-line

// #region Props, Emits
const props = defineProps({
  report: {
    type: Object,
    required: true,
  },
});
// #endregion

// #region  Params, Ref, Reactives, Computed


const pageSize = ref(50);
const currentPage = ref(1);


const toast = useToast();
const topSectionRef = ref(null);
const filteredTopSectionFilters = ref([]);
const leftSectionRef = ref(null);
const filteredLeftSectionFilters = ref([]);
const tableSectionRef = ref(null);
const firstLoadDone = ref(false);
const tableColumns = ref([]);
const vanillaColumns = ref([]);
const vanillaRows = ref([]);
const loading = ref(true);
const tableWidth = computed(() => {
  return filteredLeftSectionFilters.value.length > 0
    ? "col-12 sm:col-8 md:col-9 table-section-width"
    : "col-12";
});
// #endregion

// #region Functions
function defineFilters() {
  if (isMobile()) {
    filteredTopSectionFilters.value = props.report.filtro;
  } else {
    filteredTopSectionFilters.value = props.report.filtro.filter(
      (f) => f.ubicacion == "Superior"
    );

    filteredLeftSectionFilters.value = props.report.filtro.filter(
      (f) => f.ubicacion == "Lateral"
    );
  }
}

function defineTableColumns(headers) {
  tableColumns.value = [];

  headers.forEach((header) => {
    tableColumns.value.push({
      label: header,
      field: header,
      sortable: false,
    });
  });
}

function defineTableRows(params) {  
  let data = [];

  vanillaRows.value.forEach((row) => {
    let rowData = {};

    vanillaColumns.value.forEach((header, index) => {
      rowData[header] = row[index];
    });

    data.push(rowData);
  });

  const totalResults = data.length;

  if (params && params.pageSize !== undefined && params.pageIndex !== undefined) {
    const pageIndexZeroBased = params.pageIndex - 1;
    const start = pageIndexZeroBased * params.pageSize;
    const end = start + params.pageSize;

    return Promise.resolve({
      data: data.slice(start, end),
      pageSize: params.pageSize,
      pageIndex: params.pageIndex,
      totalResults,
    });
  }

  const start = (currentPage.value - 1) * pageSize.value;
  const end = start + pageSize.value;

  return Promise.resolve({
    data: data.slice(start, end),
    pageSize: pageSize.value,
    pageIndex: currentPage.value - 1,
    totalResults,
  });
}

async function applyFilters() {
  let topSectionRefFilters = null;
  let leftSectionRefFilters = null;
  let formattedFilters = {};

  if (topSectionRef.value) {
    topSectionRefFilters = topSectionRef.value.getFilters();
    formatFilters(topSectionRefFilters, formattedFilters);
  }

  if (leftSectionRef.value) {
    leftSectionRefFilters = leftSectionRef.value.getFilters();
    formatFilters(leftSectionRefFilters, formattedFilters);
  }

  let allFiltersFilled = true;

  Object.keys(formattedFilters).forEach((key) => {
    if (!formattedFilters[key]) {
      allFiltersFilled = false;
    }
  });

  if (!allFiltersFilled) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Por favor, llene todos los filtros",
      life: 3000,
    });
  } else {
    loading.value = true;

    const params = {
      uidInforme: props.report.idInforme,
      variables: formattedFilters,
    };

    await getReportTableData(params).then((response) => {
      if (response.status == 200) {
        vanillaColumns.value = response.data.columns;
        vanillaRows.value = response.data.data;

        defineTableColumns(vanillaColumns.value);
      }

      loading.value = false;
      firstLoadDone.value = true;
    });
  }
}

function handlerApplyFilters() {
  currentPage.value = 1;
  applyFilters();
}

function formatFilters(sectionFilters, formattedFilters) {
  sectionFilters?.filters.forEach((filter, index) => {
    if (filter.variable) {
      const clase = filter.claseFiltroDto.nombreClaseFiltro;

      if (clase == "Valor único" || clase == "Lista de valores") {
        // Replace filter.variable "'" with "" to avoid SQL injection
        const variable = filter.variable.replace(/'/g, "");
        formattedFilters[variable] = `${sectionFilters.values[index]}`;
      }

      if (clase == "Valor de rango") {
        const variable = JSON.parse(filter.variable);

        const minVariableValue = variable[0].value;
        const maxVariableValue = variable[1].value;

        if (minVariableValue) {
          const variable = minVariableValue.replace(/'/g, "");
          formattedFilters[variable] = `${sectionFilters.values[index]}`;
        }

        if (maxVariableValue) {
          const variable = maxVariableValue.replace(/'/g, "");
          formattedFilters[variable] = `${sectionFilters.ranges[index]}`;
        }
      }
    }
  });
}

async function handleDownloadRegisters() {
  let topSectionRefFilters = null;
  let leftSectionRefFilters = null;
  let formattedFilters = {};

  if (topSectionRef.value) {
    topSectionRefFilters = topSectionRef.value.getFilters();
    formatFilters(topSectionRefFilters, formattedFilters);
  }

  if (leftSectionRef.value) {
    leftSectionRefFilters = leftSectionRef.value.getFilters();
    formatFilters(leftSectionRefFilters, formattedFilters);
  }

  let allFiltersFilled = true;

  Object.keys(formattedFilters).forEach((key) => {
    if (!formattedFilters[key]) {
      allFiltersFilled = false;
    }
  });

  if (!allFiltersFilled) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Por favor, llene todos los filtros",
      life: 3000,
    });
  } else {
    loading.value = true;

    const params = {
      uidInforme: props.report.idInforme,
      variables: formattedFilters,
    };
    await downloadExecuteReport(params).then((response) => {
      if (response.status == 200) {
        downloadFile(response, `Datos Consulta.xlsx`);
      }
      loading.value = false;
    });
  }
}

function handlePageChange(event) {
  pageSize.value = event.pageSize;
  currentPage.value = event.pageIndex + 1;
  applyFilters();
}
// #endregion

// #region Effects, Hooks
onMounted(() => {
  defineFilters();

  nextTick(() => {
    applyFilters();
  });
});
// #endregion

// #region Provide, Expose
// #endregion
</script>

<style lang="scss" scoped>
@import "@/styles/_vars.scss";

.report-data-source-details {
  :deep(.light-secondary-section) {
    background-color: #f1f5f9;
  border: 1px solid #e5e7eb;
  padding: 0.35rem 0.35rem 0.25rem;
  border-radius: 8px;
  }
}

:deep(.data-view__data) {
  margin-top: 0px;
}

.report-title {
  padding: 0 1rem 0rem;
  font-size: 1.2rem;
  font-weight: bold;
  margin-top: 15px;
  margin-bottom: 8px;
}

.progress-spinner {
  height: 5rem;

  :deep(.p-progress-spinner-svg) {
    .p-progress-spinner-circle {
      stroke: $primary !important;
    }
  }
}

.left-section-container {
  padding-top: 7px;
  padding-right: 0px;
}

.left-section-container-mobile {
  // Desktop y tablet: padding-right 0
  @media (min-width: 576px) {
    padding-top: 7px;
    padding-right: 0;
  }
  
  // Mobile (col-12): solo padding-top, padding-right por defecto
  @media (max-width: 575px) {
    padding-top: 7px;
  }
}

.table-section-width {
  padding-left: 5px;
  padding-top: 7px;
}

:deep(.p-message) {
  padding: 0px;
  margin: 0px;
}

@media (min-width: 280px) and (max-width: 767px) {
  :deep(.smart-content 
  .smart-content__main
  .smart-content__center
  .data-view .data-view__header
  .data-view__header__after
  .data-view__input__container) {
    width: 0%;
  }
}
</style>
